import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
  SheetClose,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { FC, useState, useEffect } from 'react';
import { Customer, trpc } from 'src/trpc';
import { AutocompleteUser } from 'src/components/AutocompleteUser';
import { useToast } from 'src/components/useToast';
import { Plus } from 'lucide-react';
import { InvoiceItemEditor } from 'src/components/invoices/InvoiceItemEditor';
import { TotalWithTaxFooter } from 'src/components/TotalWithTaxFooter';
import { InvoiceItem } from '@libs/schemas/src/invoice';
import { DatePicker } from 'src/components/DatePicker';
import { Textarea } from '@/components/ui/textarea';
import { AddressSelector } from 'src/components/invoices/AddressSelector';

interface TaxCalculationResult {
  success: boolean;
  taxAmountInCents: number;
  totalInCents: number;
  subtotalInCents: number;
}

interface Props {
  userId?: string; // Optional: if provided, pre-selects the user
  addButtonVariant?: 'default' | 'outline';
}

export const CreateInvoiceSheet: FC<Props> = ({ userId, addButtonVariant = 'default' }) => {
  const [openSheet, setOpenSheet] = useState(false);

  const [customer, setCustomer] = useState<Customer | undefined>();
  const [selectedBillingAddressId, setSelectedBillingAddressId] = useState<string | undefined>();
  const [currentStep, setCurrentStep] = useState<'customer' | 'billing' | 'details'>('customer');
  const [taxCalculation, setTaxCalculation] = useState<TaxCalculationResult | null>(null);
  const [isCalculatingTaxes, setIsCalculatingTaxes] = useState(false);

  const [dueDate, setDueDate] = useState<Date | undefined>();
  const [notes, setNotes] = useState<string>('');
  const [items, setItems] = useState<InvoiceItem[]>([]);
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const { successToast, errorToast, infoToast } = useToast();
  const utils = trpc.useUtils();

  const { data: userData } = trpc.admin.customer.adminListCustomers.useQuery(
    { search: '', limit: 1000 },
    { enabled: !!userId },
  );

  // Tax calculation mutation
  const { mutate: calculateTaxes } = trpc.admin.invoice.calculateInvoiceTaxes.useMutation({
    onSuccess: (data) => {
      setTaxCalculation(data);
      setIsCalculatingTaxes(false);
    },
    onError: () => {
      setTaxCalculation(null);
      setIsCalculatingTaxes(false);
      // Don't show error toast for tax calculation failures as they're not critical
    },
  });

  useEffect(() => {
    if (userData?.customers && userId && !customer) {
      const foundUser = userData.customers.find((u) => u._id === userId);
      if (foundUser) {
        setCustomer(foundUser);
        setCurrentStep('billing');
      }
    }
  }, [userData, userId, customer]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!openSheet) {
      setSelectedBillingAddressId(undefined);
      setCurrentStep(userId ? 'billing' : 'customer');
      setTaxCalculation(null);
      setIsCalculatingTaxes(false);
      setDueDate(undefined);
      setNotes('');
      setItems([]);
      setShowValidationErrors(false);
      if (!userId) {
        setCustomer(undefined);
      }
    }
  }, [openSheet, userId]);

  const { mutate: createInvoice, isPending } = trpc.admin.invoice.adminCreateInvoice.useMutation({
    onSuccess: async (data) => {
      if (data.createdProducts && data.createdProducts.length > 0) {
        successToast({
          title: 'Invoice Created',
          description: 'New invoice has been successfully created.',
        });
        if (data.createdProducts && data.createdProducts.length > 0) {
          data.createdProducts.map((p) => {
            infoToast({
              title: `Product "${p.label}" Created`,
              description: `The product was automatically created.`,
            });
          });
        }
      } else {
        successToast({
          title: 'Invoice created',
          description: 'New invoice has been successfully created',
        });
      }
      setOpenSheet(false);
      utils.admin.invoice.adminListInvoices.invalidate();
    },
    onError: () => {
      errorToast({
        title: 'Failed to create invoice',
        description: 'An error occurred while creating the invoice',
      });
    },
  });

  const addItem = () => {
    const newItem: InvoiceItem = {
      label: '',
      description: '',
      quantity: 1,
      unitPriceInCents: 0,
      totalPriceInCents: 0,
      totalTaxAmountInCents: 0,
      collectTaxes: false,
    };
    setItems([...items, newItem]);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = async (
    index: number,
    changes: Record<string, number | string | boolean | undefined>,
  ) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], ...changes };

    const changedFields = Object.keys(changes);
    const shouldRecalculateTax = changedFields.some((field) =>
      ['quantity', 'unitPriceInCents', 'collectTaxes', 'totalPriceInCents'].includes(field),
    );

    if (shouldRecalculateTax) {
      const quantity = newItems[index].quantity;
      const unitPriceInCents = newItems[index].unitPriceInCents;
      newItems[index].totalPriceInCents = quantity * unitPriceInCents;
      setIsCalculatingTaxes(true);
      calculateTaxes({
        customerId: customer?._id!,
        items: newItems,
        billingAddressId: selectedBillingAddressId,
      });
    }

    // Clear validation errors if user is fixing the label
    if (
      changes.label &&
      typeof changes.label === 'string' &&
      changes.label.trim() !== '' &&
      showValidationErrors
    ) {
      setShowValidationErrors(false);
    }

    setItems(newItems);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Only run validation and submission if we're on the final step
    if (currentStep !== 'details') {
      return;
    }

    if (!customer) {
      errorToast({
        title: 'Validation Error',
        description: 'Please select a customer',
      });
      return;
    }
    if (!dueDate) {
      errorToast({
        title: 'Validation Error',
        description: 'Please select a due date',
      });
      return;
    }
    if (items.some((item) => !item.label || item.label.trim() === '')) {
      setShowValidationErrors(true);
      errorToast({
        title: 'Validation Error',
        description: 'Please fill in all product names',
      });
      return;
    }

    setShowValidationErrors(false);

    createInvoice({
      customerId: customer._id!,
      dueDate: new Date(dueDate),
      items: items,
      customerNotes: notes || undefined,
      billingAddressId: selectedBillingAddressId,
    });
  };

  const isFormValid =
    customer && dueDate && !items.some((item) => !item.label || item.label.trim() === '');

  const isStepValid = () => {
    switch (currentStep) {
      case 'customer':
        return !!customer;
      case 'billing':
        return !!customer && !!selectedBillingAddressId;
      case 'details':
        return isFormValid;
      default:
        return false;
    }
  };

  const handleCustomerSelected = (selectedCustomer: Customer) => {
    setCustomer(selectedCustomer);
    setCurrentStep('billing');
  };

  const handleBillingAddressSelected = (addressId: string) => {
    setSelectedBillingAddressId(addressId);

    // Recalculate taxes when billing address changes (if we have items with tax collection enabled)
    if (
      customer &&
      items.some((item) => item.collectTaxes && item.quantity > 0 && item.unitPriceInCents > 0)
    ) {
      setIsCalculatingTaxes(true);
      calculateTaxes({
        customerId: customer._id!,
        items,
        billingAddressId: addressId,
      });
    }
  };

  const handleContinueToDetails = () => {
    setCurrentStep('details');
  };

  const handleBackToBilling = () => {
    setCurrentStep('billing');
  };

  const handleBackToCustomer = () => {
    setCurrentStep('customer');
    setSelectedBillingAddressId(undefined);
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 'customer':
        return 'Step 1 of 3: Choose a customer to create an invoice for';
      case 'billing':
        return 'Step 2 of 3: Select or create a billing address';
      case 'details':
        return 'Step 3 of 3: Add invoice details and items';
      default:
        return '';
    }
  };

  return (
    <Sheet open={openSheet} onOpenChange={setOpenSheet}>
      <SheetTrigger asChild>
        <Button onClick={() => setOpenSheet(true)} variant={addButtonVariant} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Add Invoice
        </Button>
      </SheetTrigger>
      <SheetContent
        className="p-0 sm:max-w-[600px] lg:max-w-[700px] xl:max-w-[800px] h-full"
        onInteractOutside={(e) => {
          const hasPacContainer = e.composedPath().some((el: EventTarget) => {
            if ('classList' in el) {
              return Array.from((el as Element).classList).includes('pac-container');
            }
            return false;
          });

          if (hasPacContainer) {
            e.preventDefault();
          }
        }}
      >
        <div className="grid h-full grid-rows-[auto_1fr_auto]">
          {/* Header */}
          <SheetHeader className="border-b bg-white px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <SheetTitle className="text-left text-lg font-semibold text-gray-900">
                  Create New Invoice
                </SheetTitle>
                <p className="text-sm text-muted text-left mt-1">{getStepTitle()}</p>
              </div>
            </div>
          </SheetHeader>

          {/* Content Area - Scrollable */}
          <div className={`overflow-y-auto ${currentStep === 'customer' ? 'p-0' : 'px-6 py-6'}`}>
            <form
              onSubmit={handleSubmit}
              className={`flex flex-col ${currentStep === 'customer' ? 'h-full' : 'gap-6'}`}
            >
              {/* Step 1: Customer Selection */}
              {currentStep === 'customer' && (
                <div className="flex-1 flex flex-col min-h-0">
                  <AutocompleteUser
                    setCustomer={handleCustomerSelected}
                    selectedCustomer={customer}
                    className="flex-1"
                  />
                </div>
              )}

              {/* Step 2: Billing Address Selection */}
              {currentStep === 'billing' && customer && (
                <div className="space-y-4">
                  {/* Customer Summary */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-left text-gray-900">Customer</h3>
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-lg">{`${customer.name?.first} ${customer.name?.last}`}</p>
                          <p className="text-sm text-gray-400">Email: {customer?.email}</p>
                          <p className="text-sm text-gray-400">Phone: {customer?.phone}</p>
                        </div>
                        {!userId && (
                          <Button type="button" variant="outline" onClick={handleBackToCustomer}>
                            Change Customer
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Billing Address Selection */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-left text-gray-900">
                      Billing Address
                    </h3>
                    <AddressSelector
                      userId={customer.userId!}
                      selectedAddressId={selectedBillingAddressId}
                      onAddressSelect={handleBillingAddressSelected}
                      addressType="all"
                    />
                  </div>
                </div>
              )}

              {/* Step 3: Invoice Details */}
              {currentStep === 'details' && customer && (
                <>
                  {/* Customer and Billing Address Summary */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-left text-gray-900">Summary</h3>
                    <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Customer</p>
                        <p className="text-sm text-gray-600">
                          {`${customer.name?.first} ${customer.name?.last}`} - {customer?.email}
                        </p>
                        {!userId && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={handleBackToCustomer}
                            className="mt-1 p-0 h-auto text-primary"
                          >
                            Change Customer
                          </Button>
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Billing Address</p>
                        <p className="text-sm text-gray-600">
                          {selectedBillingAddressId ? 'Address selected' : 'No address selected'}
                        </p>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handleBackToBilling}
                          className="mt-1 p-0 h-auto text-primary"
                        >
                          Change Billing Address
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-left text-gray-900">
                      Invoice Details
                    </h3>
                    <div className="border border-gray-200 space-y-2 rounded-lg p-4 flex flex-col gap-2">
                      <div className="flex flex-col gap-2">
                        <label className="label text-xs" htmlFor="due-date">
                          Due Date
                        </label>
                        <DatePicker date={dueDate} setDate={setDueDate} fromDate={new Date()} />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="label text-xs" htmlFor="due-notes">
                          Customer Notes
                        </label>
                        <Textarea
                          value={notes || ''}
                          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                            setNotes(e.target.value || '')
                          }
                          placeholder="Add notes to the invoice..."
                          className="w-full p-3 border border-gray-300 rounded-md min-h-[100px] resize-y  text-text-light"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-left text-gray-900">Invoice Items</h3>

                    {items.map((item: InvoiceItem, index: number) => (
                      <div key={index}>
                        <InvoiceItemEditor
                          item={item}
                          index={index}
                          onUpdate={updateItem}
                          onRemove={removeItem}
                          showValidationError={showValidationErrors}
                        />
                      </div>
                    ))}

                    <div className="flex justify-end">
                      <Button variant="outline" size="sm" onClick={addItem} type="button">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Item
                      </Button>
                    </div>
                  </div>

                  <TotalWithTaxFooter
                    subtotalInCents={taxCalculation?.subtotalInCents || 0}
                    taxAmountInCents={taxCalculation?.taxAmountInCents || 0}
                    totalInCents={taxCalculation?.totalInCents || 0}
                    isCalculatingTaxes={isCalculatingTaxes}
                  />
                </>
              )}
            </form>
          </div>

          <SheetFooter className="border-t bg-gray-50 px-6 py-4">
            <div className="flex w-full justify-between gap-4">
              <div className="flex gap-2">
                {currentStep === 'billing' && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleBackToCustomer}
                    disabled={!!userId}
                  >
                    Back
                  </Button>
                )}
                {currentStep === 'details' && (
                  <Button type="button" variant="outline" onClick={handleBackToBilling}>
                    Back
                  </Button>
                )}
              </div>
              <div className="flex gap-4">
                <SheetClose asChild>
                  <Button variant="outline" className="w-full sm:w-auto">
                    Cancel
                  </Button>
                </SheetClose>
                {currentStep === 'billing' && (
                  <Button
                    type="button"
                    className="w-full sm:w-auto text-white"
                    onClick={handleContinueToDetails}
                  >
                    Continue
                  </Button>
                )}
                {currentStep === 'details' && (
                  <Button
                    disabled={!isStepValid() || isPending}
                    className="w-full sm:w-auto text-white"
                    type="submit"
                    onClick={handleSubmit}
                  >
                    {isPending ? 'Creating Invoice...' : 'Create Invoice'}
                  </Button>
                )}
              </div>
            </div>
          </SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  );
};
